'use client';

import React, { useState, useMemo, useEffect } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import {
  Dialog,
  Button,
  Input,
  Checkbox,
  Search,
  LoadingState,
} from 'src/components';
import { Tooltip } from 'src/components/PulseUI';
import { useAuth } from 'src/context/auth';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';
import { useSelector, useDispatch as useReduxDispatch } from 'react-redux';
import { selectDiscoveryMapCurrentTerritory, setCurrentTerritory as setMapCurrentTerritory } from 'src/redux/slices/discoveryMapSlice';
import { createTerritory, getTerritory } from 'src/hooks/medscoutmap/territoryApi';
import { useAppDispatch } from 'src/redux';
import { territoryApi, useGetCompanyUsersQuery } from 'src/api/redux';
import { TERRITORY_TAG_TYPE } from 'src/api/redux/territories/constants';
import { twMerge } from 'tailwind-merge';
import { calculateCirclePolygon } from 'src/components/Discovery/utils/calculateCirclePolygon';
import { useDebounce } from 'use-debounce';
import { convertToLegacyPolygonFormat } from 'src/utils/territory';
interface CopyTerritoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  territory?: any;
}

const CopyTerritoryModal = ({
  isOpen,
  onClose,
  territory,
}: CopyTerritoryModalProps) => {
  const { user, setUser } = useAuth();
  const log = useLogging();
  const { setNotification } = useNotification();
  const dispatch = useAppDispatch();
  const mapDispatch = useReduxDispatch();
  const mapCurrentTerritory = useSelector(selectDiscoveryMapCurrentTerritory);
  const [copyName, setCopyName] = useState('');
  const [selectedReps, setSelectedReps] = useState<string[]>([]);
  const [isCopying, setIsCopying] = useState(false);
  const [isCanonical, setIsCanonical] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);

  const [currentTerritory, setCurrentTerritory] =
    useState<MedScout.Territory | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [failedCopies, setFailedCopies] = useState<
    Array<{
      repId: string;
      repName: string;
      error: any;
      errorMessage: string;
    }>
  >([]);

  const { data: allUsersData } = useGetCompanyUsersQuery(
    {
      companyId: user?.company?.id?.toString(),
      q: debouncedSearchTerm || undefined,
      page: 1,
      pageSize: 50,
    },
    {
      skip: !user || !user.company?.id,
    }
  );

  useEffect(() => {
    const fetchTerritory = async () => {
      if (!territory) return;
      try {
        setIsLoading(true);
        const territoryData = await getTerritory(territory);
        setCurrentTerritory(territoryData);
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching territory:', error);
      }
    };
    fetchTerritory();
  }, [territory]);

  // Initialize values when dialog opens
  useEffect(() => {
    if (isOpen && currentTerritory) {
      setCopyName(`Copy of ${currentTerritory.name || ''}`);
      setSelectedReps([user?.id?.toString() || '']);
      setIsCanonical(false);
      setSearchTerm('');
      setFailedCopies([]);
    }
  }, [isOpen, currentTerritory, user?.id]);

  const filteredReps = useMemo(() => {
    if (!allUsersData) return [];

    return allUsersData?.results
      .map((user) => ({
        value: user.id,
        label: `${user.first_name} ${user.last_name}`,
      }))
      .filter(
        (rep, index, self) =>
          index === self.findIndex((t) => t.value === rep.value)
      )
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allUsersData]);

  const handleCopyNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCopyName(e.target.value);
  };

  const handleRepCheckboxChange = (repId: string) => {
    if (selectedReps.includes(repId)) {
      setSelectedReps((prev) => prev.filter((id) => id !== repId));
    } else {
      setSelectedReps((prev) => [...prev, repId]);
    }
  };

  const handleCheckBoxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsCanonical(e.target.checked);
  };

  const handleCloseCopyDialog = () => {
    onClose();
    setCurrentTerritory(null);
    setIsLoading(false);
  };

  const handleBuildTerritoryData = async (repId: string) => {
    const source = currentTerritory.source;

    // Create base territory data with common properties
    const territoryData: Record<string, any> = {
      name: copyName,
      user_id: repId,
      user: repId,
      is_canonical: isCanonical,
    };

    const hasStates = source.states?.length > 0;
    const hasCounties = source.counties?.length > 0;
    const hasZipCodes = source.zip_codes?.length > 0;
    const hasCircles = source.circles?.length > 0;
    const isDrawn = source.drawn;

    // Boundary-based territories: gather ALL geometry IDs across source types
    if (hasStates || hasCounties || hasZipCodes) {
      const geometryIds: string[] = [];

      if (hasStates) {
        geometryIds.push(
          ...source.states.map((item: any) => item.id)
        );
      }
      if (hasCounties) {
        geometryIds.push(
          ...source.counties.map((item: any) => item.id)
        );
      }
      if (hasZipCodes) {
        geometryIds.push(
          ...source.zip_codes.map((item: any) => item.id)
        );
      }

      territoryData['geometry'] = geometryIds;

      // Preserve excluded boundaries
      const excludedCountyIds = source.excluded_counties?.map(
        (item: any) => item.id
      ) || [];
      const excludedZipIds = source.excluded_zip_codes?.map(
        (item: any) => item.id
      ) || [];
      const excludeGeometryIds = [...excludedCountyIds, ...excludedZipIds];

      if (excludeGeometryIds.length > 0) {
        territoryData['exclude_geometry'] = excludeGeometryIds;
      }
    } else if (hasCircles) {
      // Radius territories: copy all circles and their polygon points
      territoryData['circles'] = source.circles.map((circle: any) => ({
        center: circle.center,
        radius: circle.radius,
      }));

      const allPolygonPoints = source.circles.map((circle: any) => {
        const polygonPoints = calculateCirclePolygon(
          circle.center,
          circle.radius,
          100
        );

        const head = polygonPoints?.[0];
        const tail = polygonPoints?.[polygonPoints?.length - 1];

        if (!head || !tail) {
          throw new Error(
            'There are no points to create the territory, please create radius'
          );
        }

        if (head.lat !== tail.lat || head.lng !== tail.lng) {
          polygonPoints.push(head);
        }

        return polygonPoints;
      });

      territoryData['points'] = allPolygonPoints;
    } else if (isDrawn) {
      // Drawn territories: copy all polygon points
      const territoryPolygons =
        currentTerritory.polygons || currentTerritory.old_polygons || [];

      if (territoryPolygons.length === 0) {
        throw new Error('Draw territory has no points data');
      }

      // Convert to legacy format for API submission (creates copies, safe to mutate)
      const polygonsForApi = convertToLegacyPolygonFormat(territoryPolygons);

      // Ensure each polygon is closed (first point equals last point)
      for (const polygon of polygonsForApi) {
        const head = polygon[0];
        const tail = polygon[polygon.length - 1];
        if (head.lat !== tail.lat || head.lng !== tail.lng) {
          polygon.push(head);
        }
      }

      territoryData['points'] = polygonsForApi;
    } else {
      throw new Error('Unknown territory type');
    }

    return territoryData;
  };

  const handleSubmit = async () => {
    if (!currentTerritory || selectedReps.length === 0 || !copyName) {
      return;
    }

    setIsCopying(true);
    const successfulCopies = [];
    const newFailedCopies = [];

    try {
      // Process each selected rep
      for (const repId of selectedReps) {
        try {
          // Build the territory data for the rep
          const territoryData = await handleBuildTerritoryData(repId);

          // Create the new territory
          const newTerritory = await createTerritory(territoryData);
          successfulCopies.push({
            territory: newTerritory,
            repId,
            repName:
              filteredReps.find((r) => r.value.toString() === repId)?.label ||
              repId,
          });
        } catch (err) {
          // Log the error but continue processing other reps
          const repName =
            filteredReps.find((r) => r.value.toString() === repId)?.label ||
            repId;
          newFailedCopies.push({
            repId,
            repName,
            error: err,
            errorMessage: err.error_message || 'Unknown error occurred',
          });
          log.exception('Error copying territory for rep', {
            tags: {
              source: 'CopyTerritoryModal',
              repId,
              error: err,
            },
          });
        }
      }

      // Update failed copies state
      setFailedCopies(newFailedCopies);

      // Set the current territory to the last one created (usually the user's copy)
      if (successfulCopies.length > 0) {
        const userCopy = successfulCopies.find(
          (copy) => copy.repId === user?.id?.toString()
        );

        if (userCopy) {
          // Only update map's currentTerritory if we're copying the currently active territory
          if (mapCurrentTerritory?.id === currentTerritory?.id) {
            mapDispatch(setMapCurrentTerritory(userCopy.territory));
          }
          setUser({
            ...user,
            last_territory: userCopy.territory,
          });
        }
      }

      // Log event and show notification
      log.event('Territories Copied', {
        source: 'CopyTerritoryModal',
        originalId: currentTerritory.id,
        successful: successfulCopies.length,
        failed: newFailedCopies.length,
        reps: selectedReps,
      });

      // Show appropriate notification
      if (successfulCopies.length > 0 && newFailedCopies.length === 0) {
        setNotification({
          title: 'Success',
          message: `Territory copied to ${successfulCopies.length} ${
            successfulCopies.length === 1 ? 'rep' : 'reps'
          }`,
          type: 'success',
        });
        // Close dialog and refresh territories list only if all copies succeeded
        dispatch(
          territoryApi.util.invalidateTags([{ type: TERRITORY_TAG_TYPE }])
        );
        handleCloseCopyDialog();
      } else if (successfulCopies.length > 0 && newFailedCopies.length > 0) {
        setNotification({
          title: 'Partial Success',
          message: `Territory copied to ${successfulCopies.length} ${
            successfulCopies.length === 1 ? 'rep' : 'reps'
          }, but failed for ${newFailedCopies.length} ${
            newFailedCopies.length === 1 ? 'rep' : 'reps'
          }`,
          type: 'warning',
        });
        // Don't close dialog if there were failures
      } else {
        setNotification({
          title: 'Error',
          message: `Failed to copy territory to any reps`,
          type: 'error',
        });
        // Don't close dialog if all copies failed
      }
    } catch (err) {
      log.exception('Error in copy process', {
        tags: {
          source: 'CopyTerritoryModal',
          error: err,
        },
      });

      setNotification({
        title: 'Error',
        message: 'An unexpected error occurred while copying territories',
        type: 'error',
      });
    } finally {
      setIsCopying(false);
      // Only refresh territories list, don't close dialog
      dispatch(
        territoryApi.util.invalidateTags([{ type: TERRITORY_TAG_TYPE }])
      );
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleCloseCopyDialog}
      size="lg"
      className="h-auto max-h-screen"
      hideBackdrop={true}
      disableClickOutside={true}
    >
      <Dialog.Header>
        <div className="flex items-center justify-between">
          <span
            className="text-base font-semibold text-primary"
            data-testid="copy-territory-modal-title"
          >
            Copy Territory
          </span>
          <Icon icon={faXmark} onClick={handleCloseCopyDialog} />
        </div>
      </Dialog.Header>
      <Dialog.Content>
        {isLoading ? (
          <div className="flex items-center justify-center p-4">
            <LoadingState size="lg" />
          </div>
        ) : (
          <div className="flex flex-col gap-4 p-4">
            <p className="text-sm text-gray-600">
              Create copies of this territory for one or more reps.
            </p>

            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <label className="block text-sm font-normal text-primary">
                  Territory Name
                </label>
                <Input
                  autoFocus
                  className="rounded-full w-full"
                  value={copyName}
                  onChange={handleCopyNameChange}
                  onFocus={(e) => e.target.select()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }}
                  placeholder="Enter name for copied territory"
                  size="sm"
                  data-testid="copy-territory-name-input"
                />
              </div>

              <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-normal text-primary">
                    Assign To (select one or more)
                  </label>
                  <p className="text-xs text-neutral-600">
                    {selectedReps.length} selected
                  </p>
                </div>

                <div className="flex items-center gap-2 pb-2">
                  <Search
                    className={twMerge(
                      '!rounded-full w-full',
                      'border border-gray-200'
                    )}
                    placeholder="Search users..."
                    size="sm"
                    onSearch={(value) => setSearchTerm(value)}
                    data-testid="copy-territory-user-search"
                  />
                </div>

                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
                  <div className="flex items-center p-2 hover:bg-gray-50">
                    <Checkbox
                      size="sm"
                      color="primary"
                      id={`copy-rep-${user?.id}`}
                      checked={selectedReps.includes(
                        user?.id?.toString() || ''
                      )}
                      onChange={() =>
                        handleRepCheckboxChange(user?.id?.toString() || '')
                      }
                      data-testid={`copy-territory-rep-checkbox-${user?.id}`}
                    />
                    <label
                      htmlFor={`copy-rep-${user?.id}`}
                      className="pl-2 text-xs text-gray-900"
                    >
                      Me ({user?.first_name} {user?.last_name})
                    </label>
                  </div>

                  {filteredReps.map((rep) => {
                    if (rep.value.toString() === user?.id?.toString())
                      return null;
                    return (
                      <div
                        key={rep.value}
                        className="flex items-center p-2 hover:bg-gray-50"
                      >
                        <Checkbox
                          size="sm"
                          color="primary"
                          id={`copy-rep-${rep.value}`}
                          checked={selectedReps.includes(rep.value.toString())}
                          onChange={() =>
                            handleRepCheckboxChange(rep.value.toString())
                          }
                          data-testid={`copy-territory-rep-checkbox-${rep.value}`}
                        />
                        <label
                          htmlFor={`copy-rep-${rep.value}`}
                          className="pl-2 text-xs text-gray-900"
                        >
                          {rep.label}
                        </label>
                      </div>
                    );
                  })}

                  {searchTerm && filteredReps.length === 0 && (
                    <div className="p-2 text-center text-xs text-neutral-500">
                      No users found matching &quot;{searchTerm}&quot;
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  size="sm"
                  color="primary"
                  checked={isCanonical}
                  onChange={handleCheckBoxChange}
                  data-testid="discovery-territory-is-canonical"
                />
                <span className="text-xs font-normal text-primary">
                  Make primary territory
                </span>
                <Tooltip
                  content="Make this territory a primary territory for the selected user above. Only managers can perform this action."
                  position="top"
                  slotProps={{ contentClassName: 'max-w-64' }}
                >
                  <Icon
                    icon={faCircleInfo}
                    className="w-3.5 h-3.5 text-secondary"
                  />
                </Tooltip>
              </div>

              {/* Error messages section */}
              {failedCopies.length > 0 && (
                <div
                  className="p-2 bg-red-50 border border-red-200 rounded-lg"
                  data-testid="copy-territory-error-container"
                >
                  <div className="text-xs font-medium text-red-700">
                    Failed to copy territory for:
                  </div>
                  <div className="pt-2">
                    {failedCopies.map((failure, index) => (
                      <div
                        key={index}
                        className="text-xs text-red-600 flex items-start gap-2"
                        data-testid="copy-territory-error-message"
                      >
                        <span className="font-medium">{failure.repName}:</span>
                        <span className="text-red-600">
                          {failure.error?.error_message ||
                            failure.errorMessage ||
                            'Unknown error occurred'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </Dialog.Content>
      <Dialog.Actions>
        <div className="flex gap-2 w-full">
          <Button
            variant="outlined"
            color="neutral"
            onClick={handleCloseCopyDialog}
            className="rounded-full w-full"
            data-testid="copy-territory-cancel-button"
          >
            Cancel
          </Button>
          <Button
            variant="solid"
            color="primary"
            onClick={handleSubmit}
            loading={isCopying}
            disabled={!copyName || selectedReps.length === 0}
            className="rounded-full w-full"
            data-testid="copy-territory-submit-button"
          >
            Copy
          </Button>
        </div>
      </Dialog.Actions>
    </Dialog>
  );
};

export default CopyTerritoryModal;
